import {
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  InputAdornment,
  Paper,
  Divider,
  Stack,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Phone,
  Lock,
  CheckCircle,
  VpnKey,
  HowToReg,
  CloudUpload,
  ErrorOutline,
  InfoOutlined,
  ArrowForward
} from "@mui/icons-material";
import { FC, useEffect, useState } from "react";
import { CaseCreationFormArr } from "./Data";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import { commonFormSwitch, ControlledBoxSizeSwitch } from "Utils/SwitchCases";
import {
  defaultDOB,
  FormatDropdownData,
  HandleFormChange,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  useSavedData,
  ValidateFormData,
} from "Utils/Common";
import ImgPath from "Constants/Imgs";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { Form, useNavigate, useLocation } from "react-router-dom";
import { RouteUrls } from "Constants/RouteUrls";
import CaseApi from "Service/Case.api";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../../../../Redux/Screen/Action";
import ControlledForm from "Components/ControlledForm";
import ModalLayout from "Layouts/Modal";
import PaymentApi from "Service/Payment.api";
import AdminApi from "Service/Admin.api";
import AuthApi from "Service/Auth.api";

const CaseCreation: FC = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<FormDataType[]>(CaseCreationFormArr);
  const [bulkUpload, setBulkUpload] = useState<boolean>(false);
  const [odrSelectionMode, setOdrSelectionMode] = useState<"none" | "wizard" | "bulk">("none");
  const [errorObj, setErrorObj] = useState({
    csvError: false,
    csvErrorText: "",
    zipError: false,
    zipErrorText: "",
  });
  const [caseResponse, setCaseResponse] = useState<any>([]);
  const [selectedCSVFile, setSelectedCSVFile] = useState<File | null>(null);
  const [selectedZip, setSelectedZip] = useState<File | null>(null);
  const [consentGiven, setConsentGiven] = useState<"yes" | "no">("yes");
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [filingProgress, setFilingProgress] = useState<number>(0);
  const [filingStatusText, setFilingStatusText] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const navigate = useNavigate();
  const location = useLocation();

  // 1. Add state for reward usage
  const [useRewards, setUseRewards] = useState<boolean>(false);
  const [userPointsBalance, setUserPointsBalance] = useState<number>(0);
  const [isCreatingCase, setIsCreatingCase] = useState<boolean>(false);

  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const isEMitra = myInfo?.userType?.toLowerCase() === "e_mitra_user";

  const [isNewClient, setIsNewClient] = useState<boolean>(false);
  const [newClientData, setNewClientData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    emailOtp: "",
    phoneOtp: "",
  });

  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [emailTimer, setEmailTimer] = useState(0);
  const [phoneTimer, setPhoneTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  useEffect(() => {
    let timer: any;
    if (emailTimer > 0) {
      timer = setInterval(() => setEmailTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [emailTimer]);

  useEffect(() => {
    let timer: any;
    if (phoneTimer > 0) {
      timer = setInterval(() => setPhoneTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [phoneTimer]);

  const handleSendOtp = async (type: "email" | "phone") => {
    if (type === "email") {
      if (!newClientData.email) {
        dispatch(setAlertData({ alertMessage: "Please enter email first.", alertVariant: "error", openAlert: true }));
        return;
      }
      dispatch(startLoading({ loading: true, loadtext: "Sending Email OTP..." }));
      try {
        const res = await AuthApi.emailSendOtp({ email: newClientData.email, requestType: "register" });
        if (res.success) {
          setEmailOtpSent(true);
          setEmailTimer(60);
          dispatch(setAlertData({ alertMessage: "Email OTP sent successfully.", alertVariant: "success", openAlert: true }));
        }
      } finally {
        dispatch(stopLoading());
      }
    } else {
      if (!newClientData.phone) {
        dispatch(setAlertData({ alertMessage: "Please enter phone number first.", alertVariant: "error", openAlert: true }));
        return;
      }
      dispatch(startLoading({ loading: true, loadtext: "Sending Phone OTP..." }));
      try {
        const res = await AuthApi.mobileSendOtp({ mobileNumber: newClientData.phone, requestType: "register" });
        if (res.success) {
          setPhoneOtpSent(true);
          setPhoneTimer(60);
          dispatch(setAlertData({ alertMessage: "Phone OTP sent successfully.", alertVariant: "success", openAlert: true }));
        }
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleVerifyOtp = async (type: "email" | "phone") => {
    if (type === "email") {
      if (!newClientData.emailOtp) {
        dispatch(setAlertData({ alertMessage: "Please enter Email OTP.", alertVariant: "error", openAlert: true }));
        return;
      }
      dispatch(startLoading({ loading: true, loadtext: "Verifying Email OTP..." }));
      try {
        const res = await AuthApi.verifyOtp({ email: newClientData.email, otp: newClientData.emailOtp });
        if (res.success) {
          setEmailVerified(true);
          dispatch(setAlertData({ alertMessage: "Email verified successfully.", alertVariant: "success", openAlert: true }));
        }
      } finally {
        dispatch(stopLoading());
      }
    } else {
      if (!newClientData.phoneOtp) {
        dispatch(setAlertData({ alertMessage: "Please enter Phone OTP.", alertVariant: "error", openAlert: true }));
        return;
      }
      dispatch(startLoading({ loading: true, loadtext: "Verifying Phone OTP..." }));
      try {
        const res = await AuthApi.verifyOtp({ mobileNumber: newClientData.phone, otp: newClientData.phoneOtp });
        if (res.success) {
          setPhoneVerified(true);
          dispatch(setAlertData({ alertMessage: "Phone verified successfully.", alertVariant: "success", openAlert: true }));
        }
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleRegisterClient = async () => {
    if (!newClientData.firstName || !newClientData.email || !newClientData.phone || !newClientData.password) {
      dispatch(setAlertData({ alertMessage: "Please provide all details: First Name, Email, Phone, and Password.", alertVariant: "error", openAlert: true }));
      return;
    }
    if (!emailVerified || !phoneVerified) {
      dispatch(setAlertData({ alertMessage: "Please verify both OTPs first.", alertVariant: "error", openAlert: true }));
      return;
    }

    dispatch(startLoading({ loading: true, loadtext: "Registering Client..." }));
    try {
      const payload = {
        firstName: newClientData.firstName,
        lastName: newClientData.lastName || "New",
        email: newClientData.email,
        contactNumber: newClientData.phone,
        password: newClientData.password,
        emailOtp: newClientData.emailOtp,
        phoneOtp: newClientData.phoneOtp,
      };
      const res = await AuthApi.emitraRegisterClient(payload);
      if (res.success) {
        dispatch(setAlertData({ alertMessage: "Client registered successfully!", alertVariant: "success", openAlert: true }));
        // Automatically select the new client and switch to existing user mode
        setSelectedClient({
          label: `${newClientData.firstName} ${newClientData.lastName || "New"} (${newClientData.email})`,
          value: res.userId || res.data?.userId
        });
        setIsNewClient(false);
      }
    } finally {
      dispatch(stopLoading());
    }
  };

  // 2. Fetch user points based on selected client or myInfo
  useEffect(() => {
    const fetchBalance = async () => {
      let targetUserId = 0;

      if (isEMitra) {
        if (!isNewClient && selectedClient) {
          targetUserId = selectedClient.value;
        } else {
          // If it's a new client, they have 0 points
          setUserPointsBalance(0);
          setUseRewards(false);
          return;
        }
      } else {
        targetUserId = myInfo.id || 0;
      }

      if (targetUserId > 0) {
        const res = await PaymentApi.getUserRewardBalance(targetUserId);
        if (res.success) {
          setUserPointsBalance(res.data.balance);
          // If balance is 0, uncheck logic
          if (res.data.balance <= 0) setUseRewards(false);
        } else {
          setUserPointsBalance(0);
          setUseRewards(false);
        }
      }
    };
    fetchBalance();
  }, [myInfo.id, isEMitra, selectedClient, isNewClient]);

  useEffect(() => {
    if (isEMitra) {
      const fetchClients = async () => {
        try {
          const res: any = await AdminApi.getAllUsers("USER", 1, 50, clientSearchQuery);

          let usersArray = [];
          if (res?.data?.data && Array.isArray(res.data.data)) {
            usersArray = res.data.data;
          } else if (res?.data && Array.isArray(res.data)) {
            usersArray = res.data;
          } else if (res && Array.isArray(res)) {
            usersArray = res;
          }

          const drops = usersArray.map((u: any) => ({
            label: `${u.firstName} ${u.lastName || ""} (${u.email})`,
            value: u.id
          }));

          setClientOptions(drops);
        } catch (e) {
          console.error(e);
        }
      };

      const delayDebounceFn = setTimeout(() => {
        fetchClients();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [isEMitra, clientSearchQuery]);

  useEffect(() => {
    handleOnLoad();
    // ----------------------------------------------------
    //  NEW: Cleanup Function (runs when component unmounts)
    // ----------------------------------------------------
    // return () => {
    // If the component is unmounting (user navigated away or closed the tab), 
    // clear the stored selection data.
    if (localStorage.getItem("userPurchaseData")) {
      localStorage.removeItem("userPurchaseData");
    }
    // };
  }, []);
  const getSavedData = useSavedData();

  const handleOnLoad: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    try {
      const OrderTypeDrop: DropdownArrPropsType[] = await getOrderTypes();

      const res: GlobalResType = await CaseApi.fetchCaseTypes();

      let CaseTypeDrop: DropdownArrPropsType[] = [];

      if (res?.success) {
        setCaseResponse(res.data);
      }

      let orderTypeId: number | null = getSavedData()?.orderTypeId;
      let caseTypeId: number | null = getSavedData()?.caseTypeId;

      if (orderTypeId && res?.success) {
        const allCaseTypes = res.data; // use API data directly
        const filterArr = allCaseTypes.filter(
          (el: any) => el.orderTypeId === orderTypeId
        );
        CaseTypeDrop = FormatDropdownData(filterArr, "id", "name", "id");
      }

      setFormData((prev) => {
        let mappedData = prev.map((el) => {
          // skip description field
          if (el.id === "description") {
            return el;
          }
          if (el.id === "orderType") {
            const state = location.state as any;
            const preselectOrderType = state?.preselectOrderType;
            const selectedOrder = OrderTypeDrop.find(
              (opt) => opt.value === (preselectOrderType || orderTypeId)
            );

            return {
              ...el,
              dropArr: OrderTypeDrop,
              value: selectedOrder?.value ?? "",
              dropValue: selectedOrder?.value ?? "",
            };
          }

          if (el.id === "caseType") {
            const selectedCase = CaseTypeDrop.find(
              (opt) => opt.value === caseTypeId
            );

            return {
              ...el,
              dropArr: CaseTypeDrop,
              value: selectedCase?.value ?? "",
              dropValue: selectedCase?.value ?? "",
            };
          }
          return el;
        });

        const state = location.state as any;
        const preselectOrderType = state?.preselectOrderType;
        if (preselectOrderType) {
          mappedData = [mappedData[0]];
          setOdrSelectionMode("none");
          setBulkUpload(false);
        }

        return mappedData;
      });
    } catch (error) {
      console.error("❌ Error in handleOnLoad:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const getCaseTypes: (
    orderTypeId: number
  ) => Promise<DropdownArrPropsType[]> = async (orderTypeId) => {
    const filterArr = caseResponse.filter(
      (el: any) => el.orderTypeId === orderTypeId
    );
    return FormatDropdownData(filterArr, "id", "name", "id");
  };

  const getOrderTypes: () => Promise<DropdownArrPropsType[]> = async () => {
    const res: GlobalResType = await CaseApi.fetchOrderTypes();
    if (res?.success) {
      return FormatDropdownData(res?.data, "id", "name", "id");
    }
    return [];
  };

  const handleSubmit: () => void = async () => {
    if (isEMitra && !selectedClient && !isNewClient) {
      dispatch(setAlertData({
        alertMessage: "Please select a client to create the case for.",
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    if (isEMitra && isNewClient) {
      if (!newClientData.firstName || !newClientData.email || !newClientData.phone || !newClientData.password) {
        dispatch(setAlertData({
          alertMessage: "Please provide all details: First Name, Email, Phone, and Password.",
          alertVariant: "error",
          openAlert: true,
        }));
        return;
      }
      if (!emailVerified || !phoneVerified) {
        dispatch(setAlertData({
          alertMessage: "Please verify both Email and Phone OTP before submitting.",
          alertVariant: "error",
          openAlert: true,
        }));
        return;
      }
    }

    const basePayload = {
      caseTypeId: formData[1]?.value,
      orderTypeId: formData[0]?.value,
      description: formData[2]?.value || "",
      isPurchaseWithRewards: useRewards
    };

    const payload = {
      ...basePayload,
      clientId: isEMitra ? selectedClient?.value : myInfo.id,
    };

    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (!validation?.errorCheck) {
      if (isCreatingCase) return;
      setIsCreatingCase(true);
      dispatch(startLoading({ loading: true, loadtext: "Creating case..." }));
      try {
        const res = await CaseApi.createCase(payload);

        if (res?.success) {
          localStorage.removeItem("userPurchaseData");
          // handle here 0rs payment if amount is 0rs then not redirect to payment page
          if (res?.data?.casePrice === 0) {
            dispatch(stopLoading());
            navigate(`/chat/${res?.data?.orderTypeId}`, { state: { openCaseId: res?.data?.pkCase } });
            return;
          }
          dispatch(stopLoading());
          navigate(`${res?.data?.pkCase}/${RouteUrls.payment}`);
        }
      } catch (error) {
        console.error("Error creating case:", error);
      } finally {
        setIsCreatingCase(false);
        dispatch(stopLoading());
      }
    }
  };

  const handleChange: (data: HandleChangeProps) => void = async (data) => {
    const { id, value } = data;
    let newFormData: FormDataType[] = JSON.parse(JSON.stringify(formData));
    if (id === "orderType") {
      const orderTypeName = newFormData[0].dropArr?.find(
        (el) => el.value === value
      )?.label;

      const isOdrSelected =
        value === 1 ||
        value === 2 ||
        value === 56 ||
        orderTypeName === "Online Mediation" ||
        orderTypeName === "Online Arbitration" ||
        orderTypeName === "Arbitration" ||
        orderTypeName === "Online Negotiation" ||
        orderTypeName === "Negotiation";

      if (isOdrSelected) {
        const isCorporate =
          myInfo?.userType?.toLowerCase() === "corporate_user" ||
          myInfo?.userType?.toLowerCase() === "corporateuser" ||
          myInfo?.userType?.toLowerCase() === "corporate";

        if (!isCorporate) {
          // Non-corporate users (citizens, NRI, admins, e-mitra, etc.) bypass bulk uploader and land directly inside the Interactive Wizard!
          const odrTypeParam =
            orderTypeName === "Online Mediation" || value === 1
              ? "mediation"
              : (orderTypeName === "Online Negotiation" || value === 56
                ? "negotiation"
                : "arbitration");
          navigate(`/case/case-creation/odr?type=${odrTypeParam}&orderTypeId=${value}`);
          return;
        }

        // Corporate/Enterprise/Admins are presented with a dual choice uploader vs wizard banner
        setOdrSelectionMode("none");
        setBulkUpload(false);
        if (newFormData.length > 1) {
          newFormData.splice(1, 2);
        }
      } else {
        setOdrSelectionMode("none");
        setBulkUpload(false);
        if (newFormData.length === 1) {
          newFormData.push({ ...CaseCreationFormArr[1] });
          newFormData.push({ ...CaseCreationFormArr[2] });
        }
        const orderTypeId = typeof value === "number" ? value : 0;
        const caseTypeDrop = await getCaseTypes(orderTypeId);
        if (newFormData[1]) {
          newFormData[1].dropArr = caseTypeDrop;
          newFormData[1].value = "";
        }
        if (newFormData[2]) {
          newFormData[2].value = "";
        }
      }
    }
    const updatedFormData = HandleFormChange(data, newFormData);
    setFormData(updatedFormData);
  };

  const handlePreflightValidation = async () => {
    let errors = {
      csvError: false,
      csvErrorText: "",
      zipError: false,
      zipErrorText: "",
    };
    if (!selectedCSVFile) {
      errors.csvError = true;
      errors.csvErrorText = "Please select a CSV file";
    }

    if (!selectedZip) {
      errors.zipError = true;
      errors.zipErrorText = "Please select a ZIP file";
    }
    setErrorObj(errors);

    if (errors.csvError || errors.zipError) return;

    if (selectedCSVFile && selectedZip) {
      if (selectedCSVFile.size > MAX_FILE_SIZE_BYTES) {
        setErrorObj({
          ...errorObj,
          csvError: true,
          csvErrorText: `CSV file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        });
        return;
      }

      if (selectedZip.size > MAX_FILE_SIZE_BYTES) {
        setErrorObj({
          ...errorObj,
          zipError: true,
          zipErrorText: `ZIP file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        });
        return;
      }

      const multipartFormData = new FormData();
      multipartFormData.append("csvFile", selectedCSVFile);
      multipartFormData.append("zipFile", selectedZip);

      dispatch(startLoading({ loading: true, loadtext: "Analyzing file contents & attachments..." }));
      try {
        const res = await CaseApi.validateBulkUpload(multipartFormData);
        if (res?.success) {
          setValidationResult(res.data);
          setActiveStep(1);
          dispatch(
            setAlertData({
              alertMessage: "Pre-flight validation completed successfully.",
              alertVariant: "success",
              openAlert: true,
            })
          );
        } else {
          dispatch(
            setAlertData({
              alertMessage: res?.message || "Validation failed.",
              alertVariant: "error",
              openAlert: true,
            })
          );
        }
      } catch (err: any) {
        dispatch(
          setAlertData({
            alertMessage: err?.message || "Error validating files.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleConfirmFiling = async (submitValidOnly: boolean) => {
    if (!validationResult) return;
    setIsProcessing(true);
    setActiveStep(2);
    setFilingProgress(10);
    setFilingStatusText("Initiating intake workflow...");

    const validCaseRefsOnly = submitValidOnly && validationResult.cases
      ? validationResult.cases.filter((c: any) => c.isValid).map((c: any) => c.caseRef)
      : null;

    const payload = {
      fileKey: validationResult.fileKey,
      csvFileName: validationResult.csvFileName,
      orderTypeId: formData[0]?.value,
      isConsentGivenToUs: consentGiven === "yes",
      validCaseRefsOnly: validCaseRefsOnly
    };

    try {
      setFilingProgress(30);
      setFilingStatusText("Queuing records for ingestion...");
      const res = await CaseApi.confirmBulkUpload(payload);
      if (res?.success) {
        setFilingProgress(70);
        setFilingStatusText("Ingestion pipeline started. Generating documents in background...");
        let progress = 70;
        const interval = setInterval(() => {
          progress += 5;
          if (progress >= 100) {
            clearInterval(interval);
            setFilingProgress(100);
            setFilingStatusText("Filing initiated! Redirecting to Dashboard...");
            setTimeout(() => {
              navigate(`/${RouteUrls.dashboard}`);
            }, 1500);
          } else {
            setFilingProgress(progress);
          }
        }, 300);
      } else {
        setIsProcessing(false);
        setActiveStep(1);
        dispatch(
          setAlertData({
            alertMessage: res?.message || "Filing confirmation failed.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (err: any) {
      setIsProcessing(false);
      setActiveStep(1);
      dispatch(
        setAlertData({
          alertMessage: err?.message || "Error submitting batch confirmation.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    }
  };

  const handleFormValidation = () => {
    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (validation?.errorCheck === false) {
      handleSubmit(); // Directly submit instead of opening the confirmation modal
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <ModalLayout
        content={
          <Typography>Are you sure want to create a new case?</Typography>
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: isCreatingCase ? "Loading..." : "Create Case",
            clickHandler: handleSubmit,
            disable: isCreatingCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
            disable: isCreatingCase,
          },
        ]}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="center" sx={{ width: "100%", maxWidth: "1200px", mb: 3, px: { xs: 2, md: 0 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 0 }}></Avatar>
          <Typography
            component="h1"
            color="primary.main"
            variant="h5"
            fontWeight="800"
          >
            Order Creation
          </Typography>
        </Stack>
        {/* <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => handleChange({ id: "orderType", value: 1 })}
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              px: 3,
              py: 1.2,
              fontWeight: "bold",
              background: "linear-gradient(135deg, #a24857 0%, #853744 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #853744 0%, #6d2c36 100%)"
              },
              boxShadow: "0 4px 12px rgba(162, 72, 87, 0.25)"
            }}
          >
            File ODR Mediation
          </Button>
          <Button
            variant="contained"
            onClick={() => handleChange({ id: "orderType", value: 2 })}
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              px: 3,
              py: 1.2,
              fontWeight: "bold",
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)"
              },
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)"
            }}
          >
            File ODR Arbitration
          </Button>
          <Button
            variant="contained"
            onClick={() => handleChange({ id: "orderType", value: 56 })}
            sx={{
              textTransform: "none",
              borderRadius: 2.5,
              px: 3,
              py: 1.2,
              fontWeight: "bold",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)"
              },
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)"
            }}
          >
            File ODR Negotiation
          </Button>
        </Stack> */}
      </Stack>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: { xs: 2, md: 4 },
          px: { xs: 2, md: 0 },
          maxWidth: { xs: "100%" },
          mx: "auto",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            mx: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            flex: "1 1 65%",
            maxWidth: { xs: "100%", md: "65%" },
            p: { xs: 2, md: 4 },
            borderRadius: 3,
            bgcolor: "background.paper",
          }}
        >
          {/* <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 1 }}></Avatar>
          <Typography
            component="h1"
            color="primary.main"
            variant="h5"
            sx={{ mb: 2 }}
          >
            Case Creation
          </Typography> */}

          {/* Client Selection for e_mitra_user */}
          {isEMitra && (
            <Box sx={{ width: { xs: "100%", md: "60%" }, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={<Radio checked={!isNewClient} onChange={() => setIsNewClient(false)} sx={sxRadio} />}
                  label="Select Existing User"
                />
                <FormControlLabel
                  control={<Radio checked={isNewClient} onChange={() => setIsNewClient(true)} sx={sxRadio} />}
                  label="Enter Details Manually"
                />
              </Box>

              {!isNewClient ? (
                <Autocomplete
                  options={clientOptions}
                  value={selectedClient}
                  onChange={(_, newValue) => setSelectedClient(newValue)}
                  renderInput={(params) => <TextField {...params} label="Select Existing Client" />}
                  fullWidth
                />
              ) : (
                <Paper
                  elevation={4}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: '#ffffff',
                    border: '1px solid #edf2f7',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                    <HowToReg color="primary" sx={{ fontSize: 30 }} />
                    <Box>
                      <Typography variant="h6" fontWeight="800" color="primary.main">New Client Registration</Typography>
                      <Typography variant="caption" color="text.primary">Create a secure account for the client</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 4 }} />

                  <Stack spacing={3}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="First Name *"
                        fullWidth
                        value={newClientData.firstName}
                        onChange={(e) => setNewClientData({ ...newClientData, firstName: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Person color="primary" sx={{ opacity: 0.7 }} /></InputAdornment>
                        }}
                      />
                      <TextField
                        label="Last Name"
                        fullWidth
                        value={newClientData.lastName}
                        onChange={(e) => setNewClientData({ ...newClientData, lastName: e.target.value })}
                      />
                    </Box>

                    <TextField
                      label="Email Address *"
                      fullWidth
                      value={newClientData.email}
                      disabled={emailVerified}
                      onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Email color="primary" sx={{ opacity: 0.7 }} /></InputAdornment>,
                        endAdornment: emailVerified && <InputAdornment position="end"><CheckCircle color="success" /></InputAdornment>
                      }}
                    />

                    <TextField
                      label="Phone Number *"
                      fullWidth
                      value={newClientData.phone}
                      disabled={phoneVerified}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setNewClientData({ ...newClientData, phone: value });
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Phone color="primary" sx={{ opacity: 0.7 }} /></InputAdornment>,
                        endAdornment: phoneVerified && <InputAdornment position="end"><CheckCircle color="success" /></InputAdornment>
                      }}
                    />

                    <TextField
                      label="Set Secure Password *"
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      value={newClientData.password}
                      onChange={(e) => setNewClientData({ ...newClientData, password: e.target.value })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock color="primary" sx={{ opacity: 0.7 }} /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Divider sx={{ my: 1 }}><Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ px: 1 }}>IDENTITY VERIFICATION</Typography></Divider>

                    {/* Email OTP Row */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="Email OTP *"
                        fullWidth
                        size="small"
                        value={newClientData.emailOtp}
                        disabled={emailVerified}
                        onChange={(e) => setNewClientData({ ...newClientData, emailOtp: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><VpnKey fontSize="small" color="action" /></InputAdornment>
                        }}
                      />
                      {!emailVerified ? (
                        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleSendOtp("email")}
                            disabled={emailTimer > 0}
                            sx={{ minWidth: '85px', height: '40px' }}
                          >
                            {emailTimer > 0 ? `${emailTimer}s` : (emailOtpSent ? "Resend" : "Send")}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleVerifyOtp("email")}
                            disabled={!emailOtpSent}
                            sx={{ minWidth: '85px', height: '40px' }}
                          >
                            Verify
                          </Button>
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', minWidth: '100px', justifyContent: 'center' }}>
                          <CheckCircle fontSize="small" />
                          <Typography variant="body2" fontWeight="800">VERIFIED</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Phone OTP Row */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="Phone OTP *"
                        fullWidth
                        size="small"
                        value={newClientData.phoneOtp}
                        disabled={phoneVerified}
                        onChange={(e) => setNewClientData({ ...newClientData, phoneOtp: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><VpnKey fontSize="small" color="action" /></InputAdornment>
                        }}
                      />
                      {!phoneVerified ? (
                        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleSendOtp("phone")}
                            disabled={phoneTimer > 0}
                            sx={{ minWidth: '85px', height: '40px' }}
                          >
                            {phoneTimer > 0 ? `${phoneTimer}s` : (phoneOtpSent ? "Resend" : "Send")}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleVerifyOtp("phone")}
                            disabled={!phoneOtpSent}
                            sx={{ minWidth: '85px', height: '40px' }}
                          >
                            Verify
                          </Button>
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', minWidth: '100px', justifyContent: 'center' }}>
                          <CheckCircle fontSize="small" />
                          <Typography variant="body2" fontWeight="800">VERIFIED</Typography>
                        </Box>
                      )}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleRegisterClient}
                      disabled={!emailVerified || !phoneVerified}
                      startIcon={<HowToReg />}
                      sx={{
                        mt: 2,
                        py: 2,
                        fontWeight: '800',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        boxShadow: '0 4px 14px 0 rgba(40, 49, 101, 0.39)',
                        background: 'linear-gradient(135deg, #283165 0%, #1b2246 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1b2246 0%, #0d1123 100%)',
                        },
                        '&.Mui-disabled': {
                          background: '#f1f1f1'
                        }
                      }}
                    >
                      Register & Proceed
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Box>
          )}

          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              width: "100%",
            }}
            container
            spacing={3}
            justifyContent="center"
          >
            {formData.map((el: FormDataType, index: number) => {
              const orderVal = formData?.[0]?.value;
              const orderTypeName = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label;
              const isOdrSelected =
                orderVal === 1 ||
                orderVal === 2 ||
                orderVal === 56 ||
                orderTypeName === "Online Mediation" ||
                orderTypeName === "Online Arbitration" ||
                orderTypeName === "Arbitration" ||
                orderTypeName === "Online Negotiation" ||
                orderTypeName === "Negotiation";

              // If ODR is selected and selection mode is none, only render the orderType select dropdown (index === 0)
              if (isOdrSelected && odrSelectionMode === "none" && index > 0) {
                return null;
              }

              return (
                <Grid key={el.id} {...ControlledBoxSizeSwitch(el.boxSize)}>
                  {commonFormSwitch({
                    formObj: el,
                    onChange: (data) => handleChange(data),
                  })}
                </Grid>
              );
            })}
          </Grid>

          {/* ODR Dual Choice Banner for Corporate/Enterprise users */}
          {(() => {
            const orderVal = formData?.[0]?.value;
            const orderTypeName = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label;
            const isOdrSelected =
              orderVal === 1 ||
              orderVal === 2 ||
              orderVal === 56 ||
              orderTypeName === "Online Mediation" ||
              orderTypeName === "Online Arbitration" ||
              orderTypeName === "Arbitration" ||
              orderTypeName === "Online Negotiation" ||
              orderTypeName === "Negotiation";

            if (isOdrSelected && odrSelectionMode === "none") {
              return (
                <Box sx={{ width: "100%", maxWidth: "600px", mt: 4, px: 2 }}>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      background: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(40, 49, 101, 0.1)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
                      textAlign: "center"
                    }}
                  >
                    <Typography variant="h6" fontWeight="800" color="primary.main" mb={1}>
                      Select ODR Filing Mode
                    </Typography>
                    <Typography variant="body2" color="text.primary" mb={4}>
                      Choose how you would like to register your dispute on the platform
                    </Typography>

                    <Stack spacing={3} direction={{ xs: "column", sm: "row" }} justifyContent="center">
                      {/* Interactive Step-by-Step Wizard */}
                      <Box
                        onClick={() => {
                          const odrTypeParam =
                            orderTypeName === "Online Mediation" || orderVal === 1
                              ? "mediation"
                              : (orderTypeName === "Online Negotiation" || orderVal === 56
                                ? "negotiation"
                                : "arbitration");
                          navigate(`/case/case-creation/odr?type=${odrTypeParam}&orderTypeId=${orderVal}`);
                        }}
                        sx={{
                          p: 3,
                          flex: 1,
                          border: "2px solid #edf2f7",
                          borderRadius: 3,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#a24857",
                            boxShadow: "0 10px 20px rgba(162, 72, 87, 0.08)",
                            transform: "translateY(-4px)"
                          }
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main" mb={1}>
                          Interactive Wizard
                        </Typography>
                        <Typography variant="caption" color="text.primary" display="block" mb={2}>
                          File your dispute step-by-step using our guided questionnaire wizard.
                        </Typography>
                        <Button variant="contained" size="small" sx={{ textTransform: "none", borderRadius: 2 }}>
                          Start Wizard
                        </Button>
                      </Box>

                      {/* Bulk CSV/ZIP Upload */}
                      <Box
                        onClick={() => {
                          setOdrSelectionMode("bulk");
                          setBulkUpload(true);
                        }}
                        sx={{
                          p: 3,
                          flex: 1,
                          border: "2px solid #edf2f7",
                          borderRadius: 3,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "#283165",
                            boxShadow: "0 10px 20px rgba(40, 49, 101, 0.08)",
                            transform: "translateY(-4px)"
                          }
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main" mb={1}>
                          Bulk CSV Upload
                        </Typography>
                        <Typography variant="caption" color="text.primary" display="block" mb={2}>
                          Import and process large batches of cases simultaneously using Excel sheets.
                        </Typography>
                        <Button variant="outlined" size="small" sx={{ textTransform: "none", borderRadius: 2 }}>
                          Upload CSV
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>
              );
            }
            return null;
          })()}

          {bulkUpload && (
            <Box sx={{ width: "100%", mt: 2, px: { xs: 0, md: 2 } }}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                <Step>
                  <StepLabel>Upload CSV & Documents</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Pre-flight Verification</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Initiate Filing Pipeline</StepLabel>
                </Step>
              </Stepper>

              {activeStep === 0 && (
                <Paper elevation={2} sx={{ p: 4, borderRadius: 3, border: "1px solid #edf2f7" }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main" mb={2}>
                    Upload Bulk Dispute Batch
                  </Typography>
                  <Box sx={{ mb: 4, p: 2.5, bgcolor: "rgba(40, 49, 101, 0.03)", borderLeft: "4px solid #283165", borderRadius: 2 }}>
                    <Typography variant="body2" color="text.primary" sx={{ mb: 1.5 }}>
                      Please select your compiled CSV dispute registry spreadsheet along with the supporting documentation ZIP archive containing PDFs.
                    </Typography>
                    <Typography variant="body2" color="primary.main">
                      💡 <strong>New to bulk filing?</strong>{" "}
                      <a
                        href="/Assets/sample_bulk_disputes.csv"
                        download="sample_bulk_disputes.csv"
                        style={{ color: "#283165", fontWeight: "bold", textDecoration: "underline" }}
                      >
                        Download Sample CSV Template
                      </a>{" "}
                      to format your dispute fields correctly and avoid validation warnings.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: "#1976d2" }}>
                      📁 <strong>ZIP Structure Guide:</strong> Your ZIP file must contain separate folders for each case. The name of each folder <strong>MUST</strong> exactly match the <code>CaseReferenceNumber</code> provided in your CSV file. Place all PDF documents for a specific case inside its corresponding folder.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ControlledForm.Input
                        id="csvSelection"
                        type="file"
                        onChange={(value) =>
                          setSelectedCSVFile(
                            typeof value == "object" ? value[0] : null
                          )
                        }
                        acceptFileTypes="text/csv"
                        required={true}
                        label="Dispute CSV Spreadsheet"
                        focused={true}
                        readOnly={isUploadSuccess}
                        error={errorObj.csvError}
                        errorText={errorObj.csvErrorText}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <ControlledForm.Input
                        id="zipSelection"
                        type="file"
                        acceptFileTypes="application/zip"
                        required={true}
                        onChange={(value) =>
                          setSelectedZip(typeof value == "object" ? value[0] : null)
                        }
                        label="Supporting Documents ZIP"
                        focused={true}
                        readOnly={isUploadSuccess}
                        error={errorObj.zipError}
                        errorText={errorObj.zipErrorText}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="body2" fontWeight="600" mb={1}>
                      Do you possess the client's consent to file these disputes?
                    </Typography>
                    <RadioGroup
                      row
                      value={consentGiven}
                      onChange={(e) => setConsentGiven(e.target.value as any)}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio sx={sxRadio} />}
                        label="Yes, consent obtained"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio sx={sxRadio} />}
                        label="No, not yet"
                      />
                    </RadioGroup>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setBulkUpload(false);
                        setOdrSelectionMode("none");
                      }}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handlePreflightValidation}
                      disabled={!consentGiven}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #283165 0%, #1b2246 100%)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #1b2246 0%, #0d1123 100%)",
                        }
                      }}
                    >
                      Validate Batch Files
                    </Button>
                  </Box>
                </Paper>
              )}

              {activeStep === 1 && validationResult && (
                <Paper elevation={2} sx={{ p: 4, borderRadius: 3, border: "1px solid #edf2f7" }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main" mb={1}>
                    Batch Pre-flight Inspection Results
                  </Typography>
                  <Typography variant="body2" color="text.primary" mb={3}>
                    Verification results for file: <strong>{validationResult.csvFileName}</strong>
                  </Typography>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 3, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#f8fafc" }}>
                        <Typography variant="h4" fontWeight="800" color="text.primary">
                          {validationResult.totalCases}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          Total Cases Ingested
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 3, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "rgba(34, 197, 94, 0.05)" }}>
                        <Typography variant="h4" fontWeight="800" color="success.main">
                          {validationResult.validCount}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          Valid & Ingestion-Ready
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ p: 3, textAlign: "center", border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: validationResult.invalidCount > 0 ? "rgba(239, 68, 68, 0.05)" : "#f8fafc" }}>
                        <Typography variant="h4" fontWeight="800" color={validationResult.invalidCount > 0 ? "error.main" : "text.secondary"}>
                          {validationResult.invalidCount}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          Validation Errors
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {validationResult.errors && validationResult.errors.length > 0 ? (
                    <Box sx={{ mb: 4 }}>
                      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        <strong>{validationResult.invalidCount} row(s) failed pre-flight verification.</strong> You can choose to skip invalid rows and initiate filing for the valid cases only, or cancel and re-upload.
                      </Alert>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary" mb={1.5}>
                        Validation Error Log
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "250px", borderRadius: 2 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Row</strong></TableCell>
                              <TableCell><strong>Case Ref</strong></TableCell>
                              <TableCell><strong>Invalid Field</strong></TableCell>
                              <TableCell><strong>Error Description</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {validationResult.errors.map((err: any, idx: number) => (
                              <TableRow key={idx} hover>
                                <TableCell>{err.row}</TableCell>
                                <TableCell>{err.caseRef || "N/A"}</TableCell>
                                <TableCell><span style={{ color: "#a24857", fontWeight: "bold" }}>{err.field}</span></TableCell>
                                <TableCell>{err.message}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
                      <strong>All cases verified successfully!</strong> High-quality data schema detected. Click below to initiate bulk ODR case processing.
                    </Alert>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setActiveStep(0);
                        setValidationResult(null);
                      }}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Cancel & Correct
                    </Button>
                    <Stack direction="row" spacing={2}>
                      {validationResult.validCount > 0 && (
                        <Button
                          variant="contained"
                          onClick={() => handleConfirmFiling(validationResult.invalidCount > 0)}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)"
                            }
                          }}
                        >
                          {validationResult.invalidCount > 0 ? `File Valid Cases Only (${validationResult.validCount})` : "Confirm & Initiate Ingestion"}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Paper>
              )}

              {activeStep === 2 && (
                <Paper elevation={2} sx={{ p: 5, borderRadius: 3, border: "1px solid #edf2f7", textAlign: "center" }}>
                  {filingProgress < 100 ? (
                    <Box sx={{ py: 4 }}>
                      <CircularProgress size={60} thickness={4.5} sx={{ mb: 3, color: "primary.main" }} />
                      <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1}>
                        Bulk Dispute File Ingestion In Progress
                      </Typography>
                      <Typography variant="body2" color="text.primary" mb={4}>
                        {filingStatusText}
                      </Typography>
                      <Box sx={{ width: "80%", mx: "auto" }}>
                        <LinearProgress variant="determinate" value={filingProgress} sx={{ height: 8, borderRadius: 4 }} />
                        <Typography variant="caption" color="text.primary" sx={{ mt: 1, display: "block" }}>
                          {filingProgress}% Completed
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ py: 4 }}>
                      <CheckCircle sx={{ fontSize: 70, color: "success.main", mb: 3 }} />
                      <Typography variant="h5" fontWeight="800" color="text.primary" mb={1.5}>
                        Bulk Filing Successfully Initiated!
                      </Typography>
                      <Typography variant="body2" color="text.primary" mb={4}>
                        We are processing the notices, e-sign links, and notifications in the background. You can track progress in real-time from your corporate dashboard.
                      </Typography>
                      <Typography variant="caption" color="text.primary" display="block">
                        Redirecting to dashboard in a moment...
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          )}

          {(() => {
            const orderVal = formData?.[0]?.value;
            const orderTypeName = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label;
            const isOdrSelected =
              orderVal === 1 ||
              orderVal === 2 ||
              orderVal === 56 ||
              orderTypeName === "Online Mediation" ||
              orderTypeName === "Online Arbitration" ||
              orderTypeName === "Arbitration" ||
              orderTypeName === "Online Negotiation" ||
              orderTypeName === "Negotiation";

            if ((isOdrSelected && odrSelectionMode === "none") || bulkUpload) {
              return null;
            }

            return (
              <Box mt={4} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  onClick={handleFormValidation}
                  sx={{
                    px: 6,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    boxShadow: '0 4px 14px 0 rgba(162, 72, 87, 0.39)',
                    background: '#283165',
                    textTransform: 'none',
                    width: { xs: "100%", sm: "80%", md: "60%" },
                    '&:hover': {
                      background: '#2a28adff',
                    }
                  }}
                >
                  Submit & Proceed
                </Button>
              </Box>
            );
          })()}
        </Paper>

        <Box
          className="summaryContainer"
          sx={{
            width: { xs: "100%", md: "30%" },
            ml: { xs: 0, md: 2 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {/* Order Summary Card */}
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "background.paper",
              position: { xs: "static", md: "sticky" },
              top: { md: 120 },
            }}
          >
            <Box sx={{ bgcolor: "#283165", p: 2, textAlign: "center" }}>
              <Typography
                variant="h6"
                color="#ffffff"
                fontWeight={600}
              >
                Order Summary
              </Typography>
            </Box>

            {/* compute labels and amounts from formData / caseResponse */}
            {(() => {
              const orderVal = formData?.[0]?.value;
              const orderTypeLabel = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label ?? "--";

              const caseVal = formData?.[1]?.value;
              const caseTypeLabel = formData?.[1]?.dropArr?.find((d: any) => d.value === caseVal)?.label ?? "--";

              let baseAmount = 0;
              let gstAmount = 0;
              let finalAmount = 0;
              let billingCycle = "";

              const POINT_VALUE = 1;
              let pointsDiscount = 0;

              if (caseVal) {
                const matched = caseResponse?.find((c: any) => String(c?.id) === String(caseVal));
                if (matched) {
                  baseAmount = matched.casePrice || 0;
                  billingCycle = matched.billingCycle || "";
                  if (baseAmount > 0) {
                    gstAmount = baseAmount * 0.18;
                    finalAmount = baseAmount + gstAmount;
                  } else {
                    finalAmount = baseAmount;
                  }
                }
              }

              // 2. Calculate Discount if rewards are toggled
              if (useRewards && userPointsBalance > 0) {
                const maxDiscountPossible = userPointsBalance * POINT_VALUE;
                const maxAllowedDiscount = baseAmount * 0.25;
                pointsDiscount = Math.min(maxDiscountPossible, maxAllowedDiscount);
              }

              const finalPayable = finalAmount - pointsDiscount;

              return (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" color="text.primary" fontWeight={600} gutterBottom>
                      Order Type
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="text.primary">
                      {orderTypeLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" color="text.primary" fontWeight={600} gutterBottom>
                      Case Type
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="text.primary">
                      {caseTypeLabel} {billingCycle ? `(${billingCycle})` : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ my: 2.5, borderTop: "1px dashed #ccc", pt: 2 }}>
                    <Grid container spacing={1} sx={{ mb: 0.5 }}>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.primary">
                          Base Price
                        </Typography>
                      </Grid>
                      <Grid size={6} textAlign="right">
                        <Typography variant="body2" fontWeight={500} color="text.primary">
                          {baseAmount > 0 ? `₹${baseAmount.toLocaleString()}` : "--"}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Only show GST if price is greater than 0 */}
                    {baseAmount > 0 && (
                      <Grid container spacing={1}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.primary">
                            GST (18%)
                          </Typography>
                        </Grid>
                        <Grid size={6} textAlign="right">
                          <Typography variant="body2" fontWeight={500} color="text.primary">
                            ₹{gstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </Box>

                  {/* 3. Reward Points Section */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(162, 72, 87, 0.05)', borderRadius: 2, border: '1px dashed rgba(162, 72, 87, 0.4)' }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={useRewards}
                          onClick={() => setUseRewards(!useRewards)}
                          disabled={userPointsBalance <= 0 || finalAmount <= 0}
                          sx={sxRadio}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="text.primary">Apply Reward Points</Typography>
                          <Typography variant="caption" color="text.primary">Available: {userPointsBalance} pts</Typography>
                        </Box>
                      }
                    />

                    {useRewards && pointsDiscount > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5, color: "#283165" }}>
                        <Typography variant="body2" fontWeight={500}>Discount Applied:</Typography>
                        <Typography variant="body2" fontWeight={700}>- ₹{pointsDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* 4. Final Total Calculation */}
                  <Box sx={{ mt: 3, pt: 2.5, borderTop: "2px solid #edf2f7" }}>
                    <Grid container alignItems="center">
                      <Grid size={6}>
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                          Total Payable
                        </Typography>
                      </Grid>
                      <Grid size={6} textAlign="right">
                        <Typography variant="h5" fontWeight={800} color="#283165">
                          {finalPayable > 0 ? `₹${finalPayable.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "Free"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              );
            })()}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseCreation;

const sxRadio = {
  color: "#a24857", // default outline color
  "&.Mui-checked": {
    color: "#a24857", // fill when selected
  },
  "&:hover": {
    backgroundColor: "rgba(128,0,0,0.08)", // maroon hover effect
  },
};
